import { useState } from 'react';
import { Loader2, X, Send, Upload, FileText, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Renders a dynamic form based on task.form_schema.
 * Supported field types: short_text, long_text, number, email, file
 * Validation: required, min_length, max_length, regex, min, max, max_size_mb, accepted_types
 */
export default function DynamicTaskForm({ task, language, isDarkMode, onClose, onSuccess }) {
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const schema = Array.isArray(task.form_schema) ? task.form_schema : [];

  const label = (f) => (language === 'ar' ? (f.label_ar || f.label_en || f.key) : (f.label_en || f.label_ar || f.key));
  const placeholder = (f) => (language === 'ar' ? (f.placeholder_ar || '') : (f.placeholder_en || ''));
  const helperText = (f) => (language === 'ar' ? (f.helper_ar || '') : (f.helper_en || ''));

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateField = (f, val) => {
    const key = f.key || f.name;
    const required = !!f.required;
    const type = (f.type || 'short_text').toLowerCase();
    const isFile = type === 'file';
    const isEmpty = val === undefined || val === null || (typeof val === 'string' && val.trim() === '') || (isFile && !val);

    if (required && isEmpty) {
      return language === 'ar' ? 'هذا الحقل مطلوب' : 'This field is required';
    }
    if (isEmpty) return null;

    if (type === 'short_text' || type === 'long_text') {
      const s = String(val);
      if (typeof f.min_length === 'number' && s.length < f.min_length) {
        return language === 'ar'
          ? `الحد الأدنى ${f.min_length} حرف`
          : `Minimum ${f.min_length} characters`;
      }
      if (typeof f.max_length === 'number' && s.length > f.max_length) {
        return language === 'ar'
          ? `الحد الأقصى ${f.max_length} حرف`
          : `Maximum ${f.max_length} characters`;
      }
      if (f.regex) {
        try {
          const re = new RegExp(f.regex);
          if (!re.test(s)) {
            return language === 'ar'
              ? (f.regex_message_ar || 'صيغة غير صحيحة')
              : (f.regex_message_en || 'Invalid format');
          }
        } catch { /* invalid regex from admin — skip */ }
      }
    }
    if (type === 'number') {
      const n = Number(val);
      if (Number.isNaN(n)) return language === 'ar' ? 'يجب أن يكون رقماً' : 'Must be a number';
      if (typeof f.min === 'number' && n < f.min) return language === 'ar' ? `الحد الأدنى ${f.min}` : `Min ${f.min}`;
      if (typeof f.max === 'number' && n > f.max) return language === 'ar' ? `الحد الأقصى ${f.max}` : `Max ${f.max}`;
    }
    if (type === 'email') {
      if (!emailRegex.test(String(val))) {
        return language === 'ar' ? 'بريد إلكتروني غير صحيح' : 'Invalid email';
      }
    }
    if (type === 'file' && val && typeof val === 'object') {
      const sizeBytes = val.size || 0;
      if (typeof f.max_size_mb === 'number' && sizeBytes > f.max_size_mb * 1024 * 1024) {
        return language === 'ar'
          ? `حجم الملف يتجاوز ${f.max_size_mb} ميجابايت`
          : `File exceeds ${f.max_size_mb} MB`;
      }
      const accepted = Array.isArray(f.accepted_types) ? f.accepted_types : [];
      if (accepted.length && val.type && !accepted.includes(val.type)) {
        return language === 'ar' ? 'نوع الملف غير مدعوم' : 'File type not supported';
      }
    }
    return null;
  };

  const validateAll = () => {
    const nextErrors = {};
    for (const f of schema) {
      const key = f.key || f.name;
      if (!key) continue;
      const err = validateField(f, values[key]);
      if (err) nextErrors[key] = err;
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleFileChange = async (field, fileInput) => {
    const file = fileInput.target.files && fileInput.target.files[0];
    if (!file) return;
    const key = field.key || field.name;
    // Immediate pre-validation of size/type
    if (typeof field.max_size_mb === 'number' && file.size > field.max_size_mb * 1024 * 1024) {
      toast.error(language === 'ar' ? `الملف أكبر من ${field.max_size_mb} ميجابايت` : `File exceeds ${field.max_size_mb} MB`);
      fileInput.target.value = '';
      return;
    }
    const accepted = Array.isArray(field.accepted_types) ? field.accepted_types : [];
    if (accepted.length && !accepted.includes(file.type)) {
      toast.error(language === 'ar' ? 'نوع الملف غير مدعوم' : 'File type not supported');
      fileInput.target.value = '';
      return;
    }
    // Base64 encode (small files only, aligned with existing CV upload pattern)
    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target.result;
      setValues((prev) => ({
        ...prev,
        [key]: { name: file.name, size: file.size, type: file.type, url }
      }));
      setErrors((prev) => ({ ...prev, [key]: null }));
    };
    reader.readAsDataURL(file);
  };

  const setValue = (key, val) => {
    setValues((prev) => ({ ...prev, [key]: val }));
    setErrors((prev) => ({ ...prev, [key]: null }));
  };

  const handleSubmit = async () => {
    if (!validateAll()) {
      toast.error(language === 'ar' ? 'يوجد حقول ناقصة أو غير صحيحة' : 'Some fields are missing or invalid');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('business_token');
      const attachments = [];
      const payloadValues = {};
      for (const f of schema) {
        const key = f.key || f.name;
        if (!key) continue;
        const v = values[key];
        if ((f.type || '').toLowerCase() === 'file' && v && v.url) {
          attachments.push({ field: key, name: v.name, size: v.size, type: v.type, url: v.url });
          payloadValues[key] = { name: v.name, size: v.size, type: v.type };
        } else if (v !== undefined && v !== null && v !== '') {
          payloadValues[key] = v;
        }
      }

      const res = await axios.post(
        `${BACKEND_URL}/api/agent/task-submissions`,
        {
          task_id: task.id,
          task_title_ar: task.title_ar,
          task_title_en: task.title_en,
          mode: 'form',
          payload: { values: payloadValues },
          attachments,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        language === 'ar'
          ? `تم إرسال طلبك بنجاح — ${res.data.request_number}`
          : `Submitted successfully — ${res.data.request_number}`
      );
      onSuccess && onSuccess(res.data);
      onClose && onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && Array.isArray(detail.validation_errors)) {
        // Map server-side errors to fields
        const mapped = {};
        for (const item of detail.validation_errors) {
          const [k, msg] = String(item).split(':');
          if (k) mapped[k.trim()] = msg ? msg.trim() : (language === 'ar' ? 'قيمة غير صحيحة' : 'Invalid value');
        }
        setErrors(mapped);
        toast.error(language === 'ar' ? 'فشل التحقق من البيانات' : 'Validation failed');
      } else {
        toast.error(
          typeof detail === 'string'
            ? detail
            : language === 'ar' ? 'حدث خطأ في الإرسال' : 'Submission error'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (f) => {
    const key = f.key || f.name;
    if (!key) return null;
    const type = (f.type || 'short_text').toLowerCase();
    const val = values[key];
    const err = errors[key];
    const commonLabelCls = `block text-sm font-semibold mb-1.5 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`;
    const inputCls = `${isDarkMode ? 'bg-[#0F172A] border-white/10 text-white placeholder:text-gray-500' : ''} ${err ? 'border-red-500 focus-visible:ring-red-500' : ''}`;

    return (
      <div key={key} data-testid={`dyn-field-${key}`}>
        <label className={commonLabelCls}>
          {label(f)}
          {f.required && <span className="text-red-500 ms-1">*</span>}
        </label>

        {type === 'short_text' && (
          <Input
            data-testid={`dyn-input-${key}`}
            value={val || ''}
            onChange={(e) => setValue(key, e.target.value)}
            placeholder={placeholder(f)}
            maxLength={f.max_length || undefined}
            className={inputCls}
          />
        )}
        {type === 'email' && (
          <Input
            type="email"
            data-testid={`dyn-input-${key}`}
            value={val || ''}
            onChange={(e) => setValue(key, e.target.value)}
            placeholder={placeholder(f) || 'name@example.com'}
            className={inputCls}
          />
        )}
        {type === 'number' && (
          <Input
            type="number"
            data-testid={`dyn-input-${key}`}
            value={val ?? ''}
            onChange={(e) => setValue(key, e.target.value)}
            placeholder={placeholder(f)}
            min={f.min}
            max={f.max}
            className={inputCls}
          />
        )}
        {type === 'long_text' && (
          <Textarea
            data-testid={`dyn-input-${key}`}
            value={val || ''}
            onChange={(e) => setValue(key, e.target.value)}
            placeholder={placeholder(f)}
            maxLength={f.max_length || undefined}
            rows={4}
            className={inputCls}
          />
        )}
        {type === 'file' && (
          <div>
            <label
              htmlFor={`file-${key}`}
              className={`flex items-center gap-3 p-3 rounded-lg border-2 border-dashed cursor-pointer transition ${
                err
                  ? 'border-red-500'
                  : isDarkMode
                    ? 'border-white/20 hover:border-green-500/50 bg-white/5'
                    : 'border-gray-300 hover:border-green-500 bg-gray-50'
              }`}
            >
              {val && val.url ? (
                <>
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm truncate ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{val.name}</p>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {(val.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <Upload className={`h-5 w-5 flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  <div className="flex-1">
                    <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {language === 'ar' ? 'اضغط لاختيار ملف' : 'Click to select a file'}
                    </p>
                    {(f.max_size_mb || (f.accepted_types || []).length) && (
                      <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {f.max_size_mb ? (language === 'ar' ? `حتى ${f.max_size_mb} ميجابايت` : `Up to ${f.max_size_mb} MB`) : ''}
                        {(f.accepted_types || []).length ? ` • ${(f.accepted_types || []).join(', ')}` : ''}
                      </p>
                    )}
                  </div>
                </>
              )}
            </label>
            <input
              id={`file-${key}`}
              type="file"
              data-testid={`dyn-input-${key}`}
              className="hidden"
              accept={(f.accepted_types || []).join(',')}
              onChange={(e) => handleFileChange(f, e)}
            />
          </div>
        )}

        {helperText(f) && !err && (
          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{helperText(f)}</p>
        )}
        {err && (
          <p className="text-xs mt-1 text-red-500 flex items-center gap-1" data-testid={`dyn-error-${key}`}>
            <AlertCircle className="h-3 w-3" />
            {err}
          </p>
        )}
      </div>
    );
  };

  const submitLabel = language === 'ar'
    ? (task.submit_button_label_ar || 'إرسال الطلب')
    : (task.submit_button_label_en || 'Submit Request');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className={`rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col ${
          isDarkMode ? 'bg-[#1E293B]' : 'bg-white'
        }`}
        onClick={(e) => e.stopPropagation()}
        data-testid="dynamic-task-form"
      >
        <div className={`p-5 border-b flex items-center justify-between flex-shrink-0 ${
          isDarkMode ? 'border-white/10' : 'border-gray-200'
        }`}>
          <div>
            <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {language === 'ar' ? task.title_ar : (task.title_en || task.title_ar)}
            </h3>
            <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {language === 'ar' ? 'يرجى تعبئة الحقول المطلوبة' : 'Please fill the required fields'}
            </p>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
            data-testid="dyn-form-close"
          >
            <X className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {(language === 'ar' ? task.description_ar : task.description_en || task.description_ar) && (
            <div className={`p-3 rounded-lg text-sm ${
              isDarkMode ? 'bg-white/5 text-gray-300' : 'bg-gray-50 text-gray-700'
            }`}>
              <FileText className="inline h-4 w-4 me-1" />
              {language === 'ar' ? task.description_ar : (task.description_en || task.description_ar)}
            </div>
          )}

          {schema.length === 0 && (
            <div className={`p-4 text-center rounded-lg ${isDarkMode ? 'bg-white/5 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
              {language === 'ar' ? 'لا توجد حقول محددة لهذه المهمة' : 'No fields defined for this task'}
            </div>
          )}

          {schema.map(renderField)}
        </div>

        <div className={`p-5 border-t flex-shrink-0 ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
          <Button
            onClick={handleSubmit}
            disabled={submitting || schema.length === 0}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
            data-testid="dyn-form-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 me-2 animate-spin" />
                {language === 'ar' ? 'جاري الإرسال...' : 'Submitting...'}
              </>
            ) : (
              <>
                <Send className="h-4 w-4 me-2" />
                {submitLabel}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
